import axiosInstance from "../../interceptor/axiosInstance";
import ApiRoutes from "../../routes/routeConstants/apiRoutes";
import { deserialize } from "serializr";
import Notification from "../../shared/components/Notification";
import { NotificationTypes } from "../../enums/notificationTypes";
import { ReportMacro } from "../../models/ReportMacro/reportMacro.model";
import { UserRoleEnum } from "../../enums/userRole.enum";

export default class ReportMacroService {
  static fetchReportMacros(
    userRole: UserRoleEnum,
    onSuccess: (macros: ReportMacro[]) => void,
    onError: () => void,
    onFinal: () => void
  ) {
    const url =
      userRole === UserRoleEnum.ADMIN
        ? ApiRoutes.REPORT_MACROS
        : ApiRoutes.REPORT_MACROS_DOCTOR;
    axiosInstance
      .get(url)
      .then((response) => {
        const raw = response.data["report_macros"] || [];
        const macros = Array.isArray(raw)
          ? raw.map((m: any) => deserialize(ReportMacro, m))
          : [deserialize(ReportMacro, raw)];
        onSuccess(macros);
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static createReportMacro(
    userRole: UserRoleEnum,
    macro: ReportMacro,
    onSuccess: (macro: ReportMacro) => void,
    onError: (error?: any) => void,
    onFinal: () => void
  ) {
    const payload = {
      report_macro: {
        shortname: macro.shortname,
        expanded_text: macro.expandedText,
      },
    };
    const url =
      userRole === UserRoleEnum.ADMIN
        ? ApiRoutes.REPORT_MACROS
        : ApiRoutes.REPORT_MACROS_DOCTOR_CREATE;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        Notification({
          message: "Macro created successfully",
          type: NotificationTypes.SUCCESS,
        });
        const created = deserialize(ReportMacro, response.data["report_macro"]);
        onSuccess(created);
      })
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        onFinal();
      });
  }

  static updateReportMacro(
    userRole: UserRoleEnum,
    macro: ReportMacro,
    onSuccess: (macro: ReportMacro) => void,
    onError: (error?: any) => void,
    onFinal: () => void
  ) {
    const payload = {
      report_macro: {
        shortname: macro.shortname,
        expanded_text: macro.expandedText,
      },
    };
    const baseUrl =
      userRole === UserRoleEnum.ADMIN
        ? ApiRoutes.REPORT_MACROS
        : ApiRoutes.REPORT_MACROS_DOCTOR_CREATE;
    const url = `${baseUrl}/${macro.id}`;
    axiosInstance
      .put(url, payload)
      .then((response) => {
        Notification({
          message: "Macro updated successfully",
          type: NotificationTypes.SUCCESS,
        });
        const updated = deserialize(ReportMacro, response.data["report_macro"]);
        onSuccess(updated);
      })
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        onFinal();
      });
  }

  static deleteReportMacro(
    userRole: UserRoleEnum,
    macroId: number,
    onSuccess: () => void,
    onError: () => void,
    onFinal: () => void
  ) {
    const baseUrl =
      userRole === UserRoleEnum.ADMIN
        ? ApiRoutes.REPORT_MACROS
        : ApiRoutes.REPORT_MACROS_DOCTOR_CREATE;
    axiosInstance
      .delete(`${baseUrl}/${macroId}`)
      .then(() => {
        Notification({
          message: "Macro deleted successfully",
          type: NotificationTypes.SUCCESS,
        });
        onSuccess();
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static bulkUploadReportMacros(
    file: File,
    onSuccess: (macros: ReportMacro[]) => void,
    onError: (error?: any) => void,
    onFinal: () => void
  ) {
    const formData = new FormData();
    formData.append("file", file);
    axiosInstance
      .post(ApiRoutes.REPORT_MACROS_BULK_UPLOAD, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        Notification({
          message: "Macros uploaded successfully",
          type: NotificationTypes.SUCCESS,
        });
        const macros = deserialize(
          ReportMacro,
          response.data["report_macros"] || []
        );
        onSuccess(Array.isArray(macros) ? macros : [macros]);
      })
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        onFinal();
      });
  }
}
